import { AddOutlined } from "@mui/icons-material";
import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import { IFormSection, TFormHookParams } from "../../types/common";
import { API_FRAUD_ALERTS, API_FRAUD_EVENTS } from "../../utils/apis";
import { FRAUD_CASES } from "../../utils/constants";
import {
  createModalFormAction,
  createViewAction,
  formatDateTime,
} from "../../utils/helpers";
import { URL_FRAUD_ALERTS } from "../../utils/urls";
import useGet from "../common/useGet";

const url = URL_FRAUD_ALERTS;

export const useFraudAlerts = () => {
  const tableColumns = () => [
    { id: "alert_id", header: "Alert ID", filter: { type: "text" } },
    { id: "event_code", header: "Event Code", filter: { type: "text" } },
    { id: "priority", header: "Priority", filter: { type: "text" } },
    { id: "status", header: "Status", filter: { type: "text" } },
    { id: "assigned_to", header: "Assigned To", filter: { type: "text" } },
    { id: "time_open", header: "Time Open", filter: { type: "text" } },
    { id: "sent_at", header: "Sent At", filter: { type: "text" } },
    {
      id: "acknowledged_at",
      header: "Acknowledged At",
      filter: { type: "text" },
    },
    { id: "event_type", header: "Event Type", filter: { type: "text" } },
    { id: "resolved_at", header: "Resolved At", filter: { type: "text" } },
    { id: "created_at", header: "Created At", filter: { type: "text" } },
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      return {
        ...item,
        event_timestamp: formatDateTime(item?.event_timestamp),
        detected_at: formatDateTime(item?.detected_at),
        sent_at: formatDateTime(item?.sent_at),
        created_at: formatDateTime(item?.created_at),
        user_info: item?.user_info?.username,
        actions: (
          <FormActions
            actions={[
              createViewAction(url, item?.id),
              createModalFormAction(
                FRAUD_CASES,
                "Add Fraud Case",
                AddOutlined,
                { alert_id: item?.id },
              ),
            ]}
          />
        ),
      };
    }),
  ];

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }
    return {
      sections: [
        {
          title: "Fraud Alert Details",
          fields: {
            alert_id: item?.alert_id,
            event_code: item?.event_code,
            priority: item?.priority,
            status: item?.status,
            title: item?.title,
            assigned_to: item?.assigned_to,
            time_open: item?.time_open,
            event_type: item?.event_type,
            sent_at: formatDateTime(item?.sent_at),
            acknowledged_at: formatDateTime(item?.acknowledged_at),
            resolved_at: formatDateTime(item?.resolved_at),
            created_at: formatDateTime(item?.created_at),
          },
        },
        {
          title: "Event Info",
          fields: {
            event_id: item?.event_info?.event_id,
            fraud_score: item?.event_info?.fraud_score,
            risk_level: item?.event_info?.risk_level,
          },
        },
        {
          title: "Created By",
          fields: {
            phone_number: item?.created_by?.phone_number,
            first_name: item?.created_by?.first_name,
            last_name: item?.created_by?.last_name,
          },
        },
      ],
    };
  };

  return {
    tableColumns,
    tableData,
    viewData,
  };
};

export const useFraudCasesForm = ({
  form,
  updateFormValue,
}: TFormHookParams = {}) => {
  const modalAlertId = form?.alert_id;

  const { data: alertDetails, loadData: loadAlertDetails } = useGet();

  const { data: eventsData, loadData: loadEvents } = useGet();

  useEffect(() => {
    if (modalAlertId) {
      loadAlertDetails({ api: `${API_FRAUD_ALERTS}${modalAlertId}/` });
      loadEvents({ api: `${API_FRAUD_EVENTS}?alert_id=${modalAlertId}` });
    }
  }, [modalAlertId]);

  useEffect(() => {
    if (alertDetails?.event_details?.event_data?.amount && updateFormValue) {
      updateFormValue(
        "estimated_loss",
        alertDetails.event_details.event_data.amount,
      );
    }
  }, [alertDetails]);

  useEffect(() => {
    if (modalAlertId && updateFormValue) {
      updateFormValue("alerts", [modalAlertId]);
    }
  }, [modalAlertId]);

  const eventsResults = eventsData?.results || [];
  const eventsOptions = Array.isArray(eventsResults)
    ? eventsResults.map((event: any) => ({
        id: event.id,
        name: `${event.event_id} - ${event.event_type} (Score: ${event.fraud_score})`,
      }))
    : [];

  const estimatedLoss = alertDetails?.event_details?.event_data?.amount || 0;

  const sections: IFormSection[] = [
    {
      title: "Fraud Case Details",
      fields: [
        {
          field_name: "title",
          label: "Title",
          type: "text",
          props: {
            required: true,
            placeholder: "Enter title...",
          },
        },
        {
          field_name: "team",
          label: "Team",
          type: "text",
          props: {
            required: true,
            placeholder: "Enter team...",
          },
        },
        {
          field_name: "description",
          label: "Description",
          type: "textarea",
          props: {
            required: true,
            placeholder: "Enter description...",
          },
        },
        {
          field_name: "events",
          label: "Events",
          type: "select",
          props: {
            required: true,
            placeholder: "Select events...",
          },
          multiple: true,
          options_data: eventsOptions,
        },
        {
          field_name: "alerts",
          label: "Alerts",
          type: "select",
          props: {
            required: false,
            placeholder: "Select alerts...",
          },
          visible: false,
          multiple: true,
        },
        {
          field_name: "severity",
          label: "Severity",
          type: "select",
          props: { required: true, placeholder: "Select severity..." },
          options_data: [
            {
              id: "LOW",
              name: "Low",
            },
            {
              id: "MEDIUM",
              name: "Medium",
            },
            {
              id: "HIGH",
              name: "High",
            },
            {
              id: "CRITICAL",
              name: "Critical",
            },
          ],
        },
        {
          field_name: "estimated_loss",
          label: "Estimated Loss",
          type: "number",
          props: {
            required: false,
            placeholder: "Enter estimated loss...",
            disabled: true,
          },
        },
      ],
    },
  ];

  return {
    sections,
    defaultValues: {
      estimated_loss: estimatedLoss,
      alerts: modalAlertId ? [modalAlertId] : [],
    },
  };
};
